/**
 * Reducer over the `MatchingConfig` draft. One action per control in the
 * setup flow. Overrides are stored only when they differ from the session's
 * source data, so "undo by hand" leaves no override behind.
 */
import { pairKey, pairOf } from './derive';
import type {
  ColumnKey,
  DoNotMatchPair,
  ColumnRuleType,
  DoNotMatchGroup,
  MatchingConfig,
  MatchingGoal,
  ParticipantId,
  SlotId,
} from './types';

export type DraftAction =
  | { type: 'replace'; config: MatchingConfig }
  | {
      type: 'setParticipantSlots';
      id: ParticipantId;
      slots: SlotId[];
      sourceSlots: SlotId[];
    }
  /** `inSource`: the pair is already in the session data (either direction). */
  | {
      type: 'addDoNotMatch';
      a: ParticipantId;
      b: ParticipantId;
      inSource: boolean;
    }
  | {
      type: 'removeDoNotMatch';
      a: ParticipantId;
      b: ParticipantId;
      inSource: boolean;
    }
  | { type: 'removeUnrecognizedDoNotMatch'; id: ParticipantId; email: string }
  | { type: 'setDoNotMatchGroup'; index: number; group: DoNotMatchGroup }
  | { type: 'addDoNotMatchGroup' }
  | { type: 'removeDoNotMatchGroup'; index: number }
  | { type: 'toggleCoachSlot'; coachId: string; slot: SlotId }
  /** `null` removes the cap. */
  | { type: 'setCoachMaxGroups'; coachId: string; maxGroups: number | null }
  | { type: 'setGroupSize'; min: number; max: number }
  | { type: 'setEnforce'; key: keyof MatchingConfig['enforce']; value: boolean }
  | { type: 'addColumnRule'; column: ColumnKey; ruleType: ColumnRuleType }
  | { type: 'toggleColumnRule'; index: number }
  | { type: 'removeColumnRule'; index: number }
  | { type: 'addGoal'; goal: MatchingGoal }
  | { type: 'updateGoal'; column: ColumnKey; goal: MatchingGoal }
  | { type: 'removeGoal'; column: ColumnKey }
  | { type: 'setRequest'; name?: string; note?: string };

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every(x => s.has(x));
}

function withOverride<T extends string[]>(
  map: Record<string, T>,
  id: string,
  value: T,
  source: T
): Record<string, T> {
  const next = { ...map };
  if (sameSet(value, source)) delete next[id];
  else next[id] = value;
  return next;
}

export function draftReducer(
  state: MatchingConfig,
  action: DraftAction
): MatchingConfig {
  switch (action.type) {
    case 'replace':
      return action.config;

    case 'setParticipantSlots':
      return {
        ...state,
        participants: {
          ...state.participants,
          availability: withOverride(
            state.participants.availability,
            action.id,
            action.slots,
            action.sourceSlots
          ),
        },
      };

    case 'addDoNotMatch':
    case 'removeDoNotMatch': {
      // Adding a session-data pair undoes its removal; removing an added pair
      // undoes the add. Either way the pair ends up stored at most once.
      const dnm = state.participants.doNotMatch;
      const key = pairKey(action.a, action.b);
      const without = (pairs: DoNotMatchPair[]) =>
        pairs.filter(([x, y]) => pairKey(x, y) !== key);
      const pair = pairOf(action.a, action.b);
      const adding = action.type === 'addDoNotMatch';
      const next = action.inSource
        ? {
            ...dnm,
            removed: adding
              ? without(dnm.removed)
              : [...without(dnm.removed), pair],
          }
        : {
            ...dnm,
            added: adding ? [...without(dnm.added), pair] : without(dnm.added),
          };
      return {
        ...state,
        participants: { ...state.participants, doNotMatch: next },
      };
    }

    case 'removeUnrecognizedDoNotMatch': {
      const dnm = state.participants.doNotMatch;
      const current = dnm.removedUnrecognized[action.id] ?? [];
      if (current.includes(action.email)) return state;
      return {
        ...state,
        participants: {
          ...state.participants,
          doNotMatch: {
            ...dnm,
            removedUnrecognized: {
              ...dnm.removedUnrecognized,
              [action.id]: [...current, action.email],
            },
          },
        },
      };
    }

    case 'setDoNotMatchGroup':
      return {
        ...state,
        doNotMatchGroups: state.doNotMatchGroups.map((g, i) =>
          i === action.index ? action.group : g
        ),
      };

    case 'addDoNotMatchGroup':
      return {
        ...state,
        doNotMatchGroups: [
          ...state.doNotMatchGroups,
          { name: `List ${state.doNotMatchGroups.length + 1}`, emails: [] },
        ],
      };

    case 'removeDoNotMatchGroup':
      return {
        ...state,
        doNotMatchGroups: state.doNotMatchGroups.filter(
          (_, i) => i !== action.index
        ),
      };

    case 'toggleCoachSlot': {
      const coach = state.coaches[action.coachId];
      if (!coach) return state;
      const slots = coach.availableSlots.includes(action.slot)
        ? coach.availableSlots.filter(s => s !== action.slot)
        : [...coach.availableSlots, action.slot];
      return {
        ...state,
        coaches: {
          ...state.coaches,
          [action.coachId]: { ...coach, availableSlots: slots },
        },
      };
    }

    case 'setCoachMaxGroups': {
      const coach = state.coaches[action.coachId];
      if (!coach) return state;
      const maxGroups =
        action.maxGroups === null ? null : Math.max(0, action.maxGroups);
      return {
        ...state,
        coaches: {
          ...state.coaches,
          [action.coachId]: { ...coach, maxGroups },
        },
      };
    }

    case 'setGroupSize':
      return {
        ...state,
        groupSize: {
          min: Math.min(action.min, action.max),
          max: Math.max(action.min, action.max),
        },
      };

    case 'setEnforce':
      return {
        ...state,
        enforce: { ...state.enforce, [action.key]: action.value },
      };

    case 'addColumnRule':
      if (state.columnRules.some(r => r.column === action.column)) return state;
      return {
        ...state,
        columnRules: [
          ...state.columnRules,
          {
            column: action.column,
            type: action.ruleType,
            enabled: true,
            builtin: false,
          },
        ],
      };

    case 'toggleColumnRule':
      return {
        ...state,
        columnRules: state.columnRules.map((r, i) =>
          i === action.index ? { ...r, enabled: !r.enabled } : r
        ),
      };

    case 'removeColumnRule':
      return {
        ...state,
        columnRules: state.columnRules.filter(
          (r, i) => i !== action.index || r.builtin
        ),
      };

    case 'addGoal':
      if (state.goals.some(g => g.column === action.goal.column)) return state;
      return { ...state, goals: [...state.goals, action.goal] };

    case 'updateGoal':
      return {
        ...state,
        goals: state.goals.map(g =>
          g.column === action.column ? action.goal : g
        ),
      };

    case 'removeGoal':
      return {
        ...state,
        goals: state.goals.filter(g => g.column !== action.column),
      };

    case 'setRequest':
      return {
        ...state,
        request: {
          name: action.name ?? state.request.name,
          note: action.note ?? state.request.note,
        },
      };
  }
}
